import { useCallback, useEffect, useState } from 'react';
import type { ChatPresenceParticipant } from '@/types/chat-presence';

/** Within 10–30s from CHAT-PRESENCE-ADMIN-PORTAL.md; 10s keeps UI closer to real-time after socketCount changes. */
const DEFAULT_POLL_MS = 10_000;

type Options = {
  enabled: boolean;
  fetchParticipants: () => Promise<ChatPresenceParticipant[] | null>;
  pollMs?: number;
  /** Bump (e.g. after Socket.IO connect/reconnect) to refetch presence immediately. */
  refreshSignal?: number;
};

/**
 * Polls chat presence over REST (no Socket.IO for presence). See docs/CHAT-PRESENCE-ADMIN-PORTAL.md
 */
export function useChatPresence({
  enabled,
  fetchParticipants,
  pollMs = DEFAULT_POLL_MS,
  refreshSignal = 0,
}: Options) {
  const [participants, setParticipants] = useState<ChatPresenceParticipant[]>([]);
  const [presenceLoading, setPresenceLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      setPresenceLoading(true);
      const rows = await fetchParticipants();
      if (rows) setParticipants(rows);
    } catch {
      /* ignore transient errors; next poll retries */
    } finally {
      setPresenceLoading(false);
    }
  }, [enabled, fetchParticipants]);

  useEffect(() => {
    if (!enabled) {
      setParticipants([]);
      return;
    }
    void load();
  }, [enabled, load, refreshSignal]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => void load(), pollMs);
    return () => window.clearInterval(id);
  }, [enabled, load, pollMs]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, load]);

  return { participants, presenceLoading, refetchPresence: load };
}
